import { Link } from "@tanstack/react-router";
import type { ComponentProps } from "react";

import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { cn } from "~/lib/utils";
import { type ContextWindowSnapshot, formatContextWindowTokens } from "~/lib/contextWindow";
import { formatProviderUsageRelativeTime } from "../usage/providerUsageRelativeTime";
import { UsageMeterBar } from "../usage/usageCard";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { COMPOSER_FOCUS_RING_CLASS } from "./composerFocusRing";
import type { ComposerPlanUsage } from "./composerPlanUsage";

type ContextWindowMeterProps = {
  readonly usage: ContextWindowSnapshot;
  readonly popupSide?: ComponentProps<typeof PopoverPopup>["side"];
  /** The thread's plan usage, when it runs on a personal Connected Subscription. */
  readonly planUsage?: ComposerPlanUsage | null;
};

function formatPercentage(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }
  if (value < 10) {
    return `${value.toFixed(1).replace(/\.0$/, "")}%`;
  }
  return `${Math.round(value)}%`;
}

function planTypeLabel(planType: string | null): string {
  if (!planType) return "";
  return ` ${planType.charAt(0).toUpperCase()}${planType.slice(1)}`;
}

function planUsageFreshness(planUsage: ComposerPlanUsage): string | null {
  if (planUsage.observedAt !== null) {
    const checked = `Checked ${formatProviderUsageRelativeTime(planUsage.observedAt)}`;
    return planUsage.stale ? `${checked} · may be out of date` : checked;
  }
  return planUsage.windows.length > 0 ? "From this thread's last turn" : null;
}

/** Account and freshness share one muted line: both qualify the figures, neither is one. */
function planUsageMeta(planUsage: ComposerPlanUsage): string | null {
  const parts = [planUsage.accountLabel, planUsageFreshness(planUsage)].filter(
    (part): part is string => part !== null && part !== "",
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * The same thresholds that produce a window's status upstream (80% used warns,
 * 100% is spent), so this bar and the Usage page's card color the same window
 * the same way.
 */
function planUsageFillClassName(usedPercent: number): string {
  if (usedPercent >= 100) return "bg-destructive";
  if (usedPercent >= 80) return "bg-warning";
  return "bg-primary";
}

/**
 * Account-scoped Usage Windows under the thread-scoped context line, the way
 * Claude Code's popover stacks them. Read-only: the figures are whatever the
 * caller already holds, and the only action is the link to Usage, where an
 * explicit Refresh lives. Each row leads with its headroom figure; the bar
 * fills by used share so an untouched window stays quiet.
 */
function PlanUsageSection({ planUsage }: { readonly planUsage: ComposerPlanUsage }) {
  const meta = planUsageMeta(planUsage);
  return (
    <div className="mt-1.5 space-y-1 border-t border-border pt-1.5">
      <div className="flex items-center justify-between gap-4">
        <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          Plan usage · {planUsage.providerLabel}
          {planTypeLabel(planUsage.planType)}
        </span>
        <Link
          to="/usage"
          className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Usage
        </Link>
      </div>
      {planUsage.windows.length === 0 ? (
        <div className="text-xs text-muted-foreground">No recent reading</div>
      ) : (
        <div className="space-y-1.5">
          {planUsage.windows.map((window) => (
            <div key={window.kind + (window.model ?? "")} className="space-y-1" data-plan-usage-row>
              <div className="flex items-baseline justify-between gap-4 whitespace-nowrap text-xs">
                <span className="text-muted-foreground">{window.label}</span>
                <span className="tabular-nums">
                  <span className="font-medium text-foreground" data-plan-usage-remaining>
                    {window.remainingPercent}% left
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    · resets {formatProviderUsageRelativeTime(window.resetsAt)}
                  </span>
                </span>
              </div>
              <UsageMeterBar
                share={Math.min(1, Math.max(0, window.usedPercent / 100))}
                fillClassName={planUsageFillClassName(window.usedPercent)}
              />
            </div>
          ))}
        </div>
      )}
      {meta ? (
        <div className="truncate text-xs text-muted-foreground" data-plan-usage-meta>
          {meta}
        </div>
      ) : null}
    </div>
  );
}

export function ContextWindowMeter({
  popupSide = "top",
  planUsage,
  usage,
}: ContextWindowMeterProps) {
  const usedPercentage = formatPercentage(usage.usedPercentage);
  const normalizedPercentage = Math.max(0, Math.min(100, usage.usedPercentage ?? 0));
  const radius = 9.75;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (normalizedPercentage / 100) * circumference;

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        closeDelay={0}
        render={
          <button
            type="button"
            // The painted ring is 24px and stays 24px: it is a readout, and a
            // bigger dial would compete with send beside it. The hit area is
            // what grows, to the touch minimum below `md` and back to the
            // footer's density above it.
            className={cn(
              RESPONSIVE_ICON_BUTTON_CLASS,
              COMPOSER_FOCUS_RING_CLASS,
              "group rounded-full transition-opacity hover:opacity-85 md:size-6 md:rounded-full",
            )}
            aria-label={
              usage.maxTokens !== null && usedPercentage
                ? `Context window ${usedPercentage} used`
                : `Context window ${formatContextWindowTokens(usage.usedTokens)} tokens used`
            }
          >
            <span className="relative flex h-6 w-6 items-center justify-center">
              <svg
                viewBox="0 0 24 24"
                className="-rotate-90 absolute inset-0 h-full w-full transform-gpu"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke="color-mix(in oklab, var(--color-muted) 70%, transparent)"
                  strokeWidth="3"
                />
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke="var(--color-muted-foreground)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className="transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
                />
              </svg>
              <span
                className={cn(
                  "relative flex h-[15px] w-[15px] items-center justify-center rounded-full bg-background text-[8px] font-medium",
                  "text-muted-foreground",
                )}
              >
                {usage.usedPercentage !== null
                  ? Math.round(usage.usedPercentage)
                  : formatContextWindowTokens(usage.usedTokens)}
              </span>
            </span>
          </button>
        }
      />
      <PopoverPopup
        tooltipStyle
        side={popupSide}
        align="end"
        className="w-max max-w-none px-3 py-2"
      >
        <div className="space-y-1 leading-tight">
          <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Context window
          </div>
          {usage.maxTokens !== null && usedPercentage ? (
            <div className="whitespace-nowrap text-xs font-medium text-foreground">
              <span>{usedPercentage}</span>
              <span className="mx-1">⋅</span>
              <span>{formatContextWindowTokens(usage.usedTokens)}</span>
              <span>/</span>
              <span>{formatContextWindowTokens(usage.maxTokens ?? null)} context used</span>
            </div>
          ) : (
            <div className="text-sm text-foreground">
              {formatContextWindowTokens(usage.usedTokens)} tokens used so far
            </div>
          )}
          {(usage.totalProcessedTokens ?? null) !== null &&
          (usage.totalProcessedTokens ?? 0) > usage.usedTokens ? (
            <div className="text-xs text-muted-foreground">
              Total processed: {formatContextWindowTokens(usage.totalProcessedTokens ?? null)}{" "}
              tokens
            </div>
          ) : null}
          {usage.compactsAutomatically ? (
            <div className="text-xs text-muted-foreground">Compacts automatically when needed.</div>
          ) : null}
          {planUsage ? <PlanUsageSection planUsage={planUsage} /> : null}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
